import "react-international-phone/style.css";
import { appendPatientTimezone } from "~/utils/patient-timezone";

import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Image,
  Input,
  Loader,
  NumberInput,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput, DatePickerInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  ActionFunctionArgs,
  createCookie,
  data as remixData,
  defer,
  HeadersArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import {
  Await,
  Form,
  isRouteErrorResponse,
  Link,
  redirect,
  useFetcher,
  useLoaderData,
  useParams,
  useRouteError,
} from "@remix-run/react";
import {
  IconArrowLeft,
  IconAt,
  IconBook,
  IconBriefcase,
  IconCalendar,
  IconCopyCheck,
  IconDiscountCheckFilled,
  IconExclamationCircle,
  IconFileTypeJpg,
  IconFileTypePdf,
  IconFileUpload,
  IconHome,
  IconInfoCircle,
  IconLock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconRefresh,
  IconRosetteDiscount,
  IconShare,
  IconStarFilled,
  IconTrash,
  IconTrophyFilled,
  IconVideo,
} from "@tabler/icons-react";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { PhoneInput } from "react-international-phone";
import { RefinementCtx, z } from "zod";

import placeholderPerson from "~/assets/misc/placeholder-person.png";
import { ConsultationServiceSelect } from "~/components/consultation-service-select";
import { DateSlot } from "~/components/date-slot";
import doctorCardClasses from "~/components/doctor-card/doctor-card.module.scss";
import { DynamicFormFields } from "~/components/dynamic-form-fields";
import { VisGoogleMap } from "~/components/google-map";
import { KikoDoctorProfileAssistant } from "~/components/kiko-doctor-profile-assistant";
import { MultiSessionScheduler } from "~/components/multi-session-scheduler";
import { PhoneNumberVerificationModal } from "~/components/phone-number-verification-modal";
import { ServiceTimeOptions } from "~/components/service-time-options";
import { Trans } from "~/components/typed-trans";
import {
  DEFAULT_OG_IMAGE_EN,
  DEFAULT_OG_IMAGE_FR,
  DOCUMENT_FILE_TYPES,
  IMAGE_FILE_TYPES,
  LARUCHE_IDENTIFIER,
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MEGABYTE,
  PAGE_TITLE,
} from "~/constants/index";
import { getGenderValues } from "~/data/dropdown-values";
import { specialtyLocationPages } from "~/data/specialty-location-pages";
import { useCountdown } from "~/hooks/use-countdown";
import { useProlongedNavigationState } from "~/hooks/use-prolonged-navigation-state";
import { TranslationKey, useTranslation } from "~/hooks/use-typed-translation";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import sharedClasses from "~/routes/($lang)._index/book-doctor.module.scss";
import { loader as serviceFormLoaderFunc } from "~/routes/api.service-form/route";
import { getAppointmentSlots } from "~/services/get-appointment-slots";
import { getDoctorProfile } from "~/services/get-doctor-profile";
import { getDoctorSpecialties } from "~/services/get-doctor-specialties";
import utilityClasses from "~/styles/utilities.module.scss";
import { ComboboxItemPrice, DoctorProfile, OkDataError } from "~/types";
import { FormField } from "~/types/form-field";
import {
  appendAccountAttribution,
  startPublicProfileSession,
  trackAccountEngagement,
} from "~/utils/account-engagement.client";
import { api } from "~/utils/api.server";
import { cleanPhoneNumber } from "~/utils/clean-phone-number";
import { shareOrCopyLink } from "~/utils/copy-to-clipboard";
import { resolveCurrencyContext } from "~/utils/currency.server";
import {
  appendDoctorAnalyticsAttribution,
  completeDoctorAnalyticsJourney,
  trackDoctorFunnelEvent,
} from "~/utils/doctor-profile-analytics.client";
import { getNameFromSlug } from "~/utils/get-name-from-slug";
import { getSlotsForDay } from "~/utils/get-slots-for-day";
import { normalizeString } from "~/utils/normalize-string";
import { renderLoadingErrorData } from "~/utils/render-loading-error-data";
import { truncateWithEllipsis } from "~/utils/truncate-text";

import NotFoundPage from "../$/route";
import classes from "./book-doctor-detail.module.scss";

function ClientOnly({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted ? <>{children}</> : null;
}

const SLOT_LOAD_TIMEOUT_MS = 12_000;

const getSlotLoadError = (
  message = "Unable to load appointment times. Please try again.",
  status = 500
) =>
  ({
    ok: false,
    data: null,
    error: {
      message,
      status,
    },
  }) satisfies OkDataError<null>;

const resolveSlotsWithTimeout = async <T,>(
  slotsPromise: Promise<OkDataError<T>>,
  timeoutMs = SLOT_LOAD_TIMEOUT_MS
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<OkDataError<null>>(resolve => {
    timeoutId = setTimeout(() => {
      resolve(
        getSlotLoadError(
          "The request took too long. Please try refreshing the page.",
          408
        )
      );
    }, timeoutMs);
  });

  try {
    return await Promise.race([slotsPromise, timeoutPromise]);
  } catch (error: any) {
    return getSlotLoadError(error?.message);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

function SlotLoadingFallback({
  loadingText,
  timeoutText,
  retryText,
}: {
  loadingText: string;
  timeoutText: string;
  retryText: string;
}) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => setTimedOut(true), SLOT_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  if (timedOut) {
    return (
      <Stack align="center" gap="md" mt="2rem">
        <IconExclamationCircle size={48} color="var(--mantine-color-red-6)" />
        <Text fz={16} ta="center" maw={400} c="red">
          {timeoutText}
        </Text>
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location.reload()}
          leftSection={<IconRefresh size={16} />}>
          {retryText}
        </Button>
      </Stack>
    );
  }

  return (
    <Center mt="3rem">
      <Stack align="center" gap="sm">
        <Loader size={30} color="var(--clr-primary-500)" />
        <Text fz={14} c="dimmed">
          {loadingText}
        </Text>
      </Stack>
    </Center>
  );
}

// i18next.t() no longer works for some reason, so I'm passing t() from the render function
const baseSchema = (t: any) =>
  z.object({
    serviceType: z
      .string()
      .nullable() // Start as null
      .refine(val => val !== null, {
        // Don't allow form to be submitted if service type is null
        message: t("service-type-required"),
      }),
    firstName: z.string().min(1, t("first-name-required")),
    surname: z.string().min(1, t("surname-required")),
    dob: z
      .date()
      .nullable() // Start as null
      .refine(val => val !== null, {
        // Don't allow form to be submitted if dob is null
        message: t("dob-required"),
      }),
    gender: z
      .string()
      .nullable() // Start as null
      .refine(val => val !== null, {
        // Don't allow form to be submitted if gender is null
        message: t("gender-required"),
      }),
    phoneNumber: z.string(),
    whatsappPhoneNumber: z.string(),
    email: z.string().email().optional().or(z.literal("")),
    serviceDate: z
      .union([z.string(), z.date()])
      .nullable(), // Start as null
    serviceTime: z.string(),
    slotId: z.string().optional(),
    additionalInformation: z.string().min(1, t("appointment-purpose-required")),
    sessions: z.array(z.object({
      session: z.number(),
      date: z.union([z.string(), z.date()]).nullable(),
      time: z.string(),
      slotId: z.string().optional(),
      dateType: z.string().optional(),
      isPredicted: z.boolean().optional()
    })).optional(),
    additionalData: z.record(z.any()).optional(),
  });

const superRefineLogic = (t: any, data: any, ctx: RefinementCtx) => {
  const isMultiSession =
    Array.isArray(data.sessions) && data.sessions.length > 1;

  if (isMultiSession) {
    data.sessions.forEach((session: any, index: number) => {
      if (!session.date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("service-date-required"),
          path: ["sessions", index, "date"],
        });
      }

      if (!session.time) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("service-time-required"),
          path: ["sessions", index, "time"],
        });
      }
    });
  } else {
    if ("serviceDate" in data && !data.serviceDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("service-date-required"),
        path: ["serviceDate"],
      });
    }

    if ("serviceTime" in data && !data.serviceTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("service-time-required"),
        path: ["serviceTime"],
      });
    }
  }

  // Only validate the field that should be filled
  const phoneNumberHasContent =
    data.phoneNumber && data.phoneNumber.trim().length > 4;
  const isPhoneNumberDisplayed = data.phoneNumber.trim().length > 0;
  const whatsappHasContent =
    data.whatsappPhoneNumber && data.whatsappPhoneNumber.trim().length > 4;
  const isWhatsappNumberDisplayed = data.whatsappPhoneNumber.trim().length > 0;

  // console.log("phoneNumberHasContent:", phoneNumberHasContent);
  // console.log("whatsappHasContent:", whatsappHasContent);

  if (!phoneNumberHasContent && isPhoneNumberDisplayed) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("at-least-one-phone-number-required"),
      path: ["phoneNumber"],
    });
    return;
  }
  if (!whatsappHasContent && isWhatsappNumberDisplayed) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("at-least-one-phone-number-required"),
      path: ["whatsappPhoneNumber"],
    });
    return;
  }

  // If phone number has content but is too short, validate it
  if (phoneNumberHasContent && data?.phoneNumber?.trim()?.length < 11) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("phone-number-too-short"),
      path: ["phoneNumber"],
    });
  }

  // If whatsapp has content but is too short, validate it
  if (whatsappHasContent && data?.whatsappPhoneNumber?.trim()?.length < 11) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("phone-number-too-short"),
      path: ["whatsappPhoneNumber"],
    });
  }
};

const schema = (t: any) =>
  baseSchema(t).superRefine((data, ctx) => superRefineLogic(t, data, ctx));

const queueSchema = (t: any) =>
  baseSchema(t)
    .omit({
      serviceDate: true,
      serviceTime: true,
    })
    .superRefine((data, ctx) => superRefineLogic(t, data, ctx));

type Inputs = z.infer<ReturnType<typeof schema>>;


import { getCentralizedMeta } from "~/utils/meta-helper";

const toAbsoluteUrl = (origin: string, value?: string | null) => {
  if (!value) return null;
  try {
    return new URL(value, origin).toString();
  } catch {
    return null;
  }
};

const getDoctorFallbackImage = (locale: string) =>
  locale === "fr" ? DEFAULT_OG_IMAGE_FR : DEFAULT_OG_IMAGE_EN;

const getDoctorAreaServed = (doctor: DoctorProfile) => {
  if (doctor.consultationModes?.includes("teleconsultation")) {
    return "Worldwide";
  }

  return doctor.licensedCountries?.length
    ? doctor.licensedCountries.map(country => ({
        "@type": "Country",
        identifier: country.toLocaleUpperCase(),
      }))
    : "Worldwide";
};

const getDoctorAggregateRating = (doctor: DoctorProfile) => {
  const average = doctor.ratingSummary?.average;
  const count = doctor.ratingSummary?.count ?? 0;

  return typeof average === "number" && count >= 3
    ? {
        "@type": "AggregateRating",
        ratingValue: average,
        ratingCount: count,
        bestRating: 5,
        worstRating: 1,
      }
    : undefined;
};

const getSpecialtyLandingPagePath = (label: string, locale: string) => {
  const normalizedLabel = normalizeString(label).toLocaleLowerCase();
  const page = specialtyLocationPages.find(specialtyPage =>
    specialtyPage.aliases.some(alias =>
      normalizedLabel.includes(normalizeString(alias).toLocaleLowerCase())
    )
  );

  return page
    ? `/${locale}/specialites/${page.specialtySlug}/${page.citySlug}`
    : null;
};

const getDoctorStructuredData = ({
  doctor,
  origin,
  locale,
  slug,
  services,
}: {
  doctor: DoctorProfile;
  origin: string;
  locale: string;
  slug: string;
  services: string;
}) => {
  const pageUrl = `${origin}/${locale}/doctor/${slug}`;
  const image = toAbsoluteUrl(origin, doctor.image) ?? getDoctorFallbackImage(locale);
  const serviceItems =
    doctor.services?.map(service => ({
      "@type": "MedicalProcedure",
      name: service.label,
      offers: service.price
        ? {
            "@type": "Offer",
            price: service.originalPrice ?? service.price,
            priceCurrency: "XOF",
          }
        : undefined,
    })) ?? [];

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${doctor.name}, ${doctor.specialization}`,
        description: doctor.briefDescription,
        inLanguage: locale,
        mainEntity: { "@id": `${pageUrl}#physician` },
      },
      {
        "@type": "Physician",
        "@id": `${pageUrl}#physician`,
        name: doctor.name,
        medicalSpecialty: doctor.specialization,
        image,
        description: doctor.briefDescription,
        url: pageUrl,
        mainEntityOfPage: { "@id": `${pageUrl}#webpage` },
        address: doctor.location
          ? {
              "@type": "PostalAddress",
              addressLocality: doctor.location,
            }
          : undefined,
        areaServed: getDoctorAreaServed(doctor),
        knowsLanguage: doctor.languages,
        hasCredential: doctor.licenseNumber
          ? {
              "@type": "EducationalOccupationalCredential",
              credentialCategory: "Medical licence",
              identifier: doctor.licenseNumber,
            }
          : undefined,
        aggregateRating: getDoctorAggregateRating(doctor),
        availableService: serviceItems.length
          ? serviceItems
          : services
              .split(",")
              .map(service => service.trim())
              .filter(Boolean)
              .map(service => ({ "@type": "MedicalProcedure", name: service })),
        priceRange: doctor.services?.some(service => service.originalPrice ?? service.price)
          ? "XOF"
          : undefined,
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: PAGE_TITLE,
            item: `${origin}/${locale}`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: locale === "fr" ? "Médecins" : "Doctors",
            item: `${origin}/${locale}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: doctor.name,
            item: pageUrl,
          },
        ],
      },
    ],
  };
};

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];

  const image = data.image ?? getDoctorFallbackImage(data.metaLocale);
  const imageAlt = data.metaLocale === "fr"
    ? `Photo de profil de ${data.metaDoctorName}`
    : `Profile photo of ${data.metaDoctorName}`;

  return getCentralizedMeta({
    title: data.metaTitle,
    description: data.metaDescription,
    image,
    imageAlt,
    imageType: data.image?.includes("la_ruche_health_profile_optimized_")
      ? "image/jpeg"
      : undefined,
    url: data.metaOrigin + location.pathname,
    locale: data.metaLocale,
    type: "profile",
    keywords: `Ivory Coast, Côte d'Ivoire, Appointment, Rendez-vous, Affordable appointment, Rendez-vous à prix abordable, Doctor near me, Médecin à proximité, ${data.metaSpecialization}, ${data.metaLocation}, ${data.metaServices}`,
    alternateLanguages: {
      en: `${data.metaOrigin}/en/doctor/${data.slug}`,
      fr: `${data.metaOrigin}/fr/doctor/${data.slug}`,
    }
  });
};

export const headers = ({ actionHeaders, loaderHeaders }: HeadersArgs) => {
  if (loaderHeaders) {
    return loaderHeaders;
  }
  // Otherwise return action headers
  return actionHeaders;
};

export default function BookAppointmentWithDoctor() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();

  const {
    data,
    ok,
    error,
    enableQueue,
    physicianSchema,
  } = useLoaderData<typeof loader>();
  // console.log("data:", data, "ok:", ok, "error:", error);

  const emailRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [dateType, setDateType] = useState("slot");
  const [serviceType, setServiceType] = useState<ComboboxItemPrice | null>(
    null
  );
  const [copied, setCopied] = useState(false);
  const [doctor] = useState<DoctorProfile | null>(data?.doctorProfileData || null);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [promoCode, setPromoCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [isPromoCodeValid, setIsPromoCodeValid] = useState("");
  const {
    isNavigating: isSubmitting,
    setIsNavigating: setIsSubmitting,
    reset: resetIsSubmitting,
  } = useProlongedNavigationState();
  const [
    phoneNumberVerificationModalOpened,
    {
      open: openPhoneNumberVerificationModal,
      close: closePhoneNumberVerificationModal,
    },
  ] = useDisclosure(false);
  const [isAllowedToBook, setIsAllowedToBook] = useState(false);
  const { isComplete, reset: resetTimer, timeLeft } = useCountdown(90, true);
  const [noWhatsappNumber, setNoWhatsappNumber] = useState(false);
  const [isQueueSubmission, setIsQueueSubmission] = useState(false);
  const [locationOption, setLocationOption] = useState("search");
  const [locationCity, setLocationCity] = useState("");
  const [locationStreet, setLocationStreet] = useState("");
  const [latLng, setLatLng] = useState("");
  const [mounted, setMounted] = useState(false);
  const [showSlotResetNotice, setShowSlotResetNotice] = useState(false);
  const previousServiceTypeRef = useRef<string | null>(null);
  const profileViewTrackedRef = useRef(false);
  const bookingTrackedRef = useRef(false);
  const slotTrackedRef = useRef(false);
  const doctorSpecialties = data.doctorSpecialtiesData ?? [];
  const ratingSummary = doctor?.ratingSummary;
  const shouldShowRating =
    typeof ratingSummary?.average === "number" && (ratingSummary?.count ?? 0) >= 3;
  const roundedRating = Math.round(ratingSummary?.average ?? 0);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!doctor?.userId || profileViewTrackedRef.current) return;
    profileViewTrackedRef.current = true;
    trackDoctorFunnelEvent("doctor_profile_viewed", {
      doctorId: doctor.userId,
      profileId: doctor.id,
      doctorSlug: slug,
      language: i18n.language,
    });
  }, [doctor?.id, doctor?.userId, i18n.language, slug]);

  useEffect(() => {
    if (!doctor?.userId) return;
    return startPublicProfileSession({
      accountId: doctor.userId,
      accountType: "doctor",
      doctorSlug: slug,
    });
  }, [doctor?.userId, slug]);

  const actionFetcher = useFetcher<typeof action>();
  const serviceFormFetcher = useFetcher<typeof serviceFormLoaderFunc>();
  const promoCodeFetcher = useFetcher<
    OkDataError<{
      isValid: boolean;
      discountPercent: number;
      services: string[];
    }>
  >();
  const doctorAsapActionFetcher = useFetcher();

  useEffect(() => {
    const response = actionFetcher.data || doctorAsapActionFetcher.data;
    if (
      !response ||
      (response as any).error ||
      !doctor?.userId ||
      bookingTrackedRef.current
    ) {
      return;
    }
    bookingTrackedRef.current = true;
    const bookingId =
      (response as any)._id ||
      (response as any).id ||
      (response as any).data?._id;
    trackAccountEngagement(
      "booking_created",
      {
        accountId: doctor.userId,
        accountType: "doctor",
        bookingId: bookingId ? String(bookingId) : undefined,
        doctorSlug: slug,
      },
      {
        eventId: bookingId ? `booking:${bookingId}` : undefined,
      }
    );
    completeDoctorAnalyticsJourney(doctor.userId);
  }, [actionFetcher.data, doctor?.userId, doctorAsapActionFetcher.data, slug]);

  const form = useForm({
    // Controlled mode allows Select to properly work when changing language and also allows service time disabling logic to work properly when service date is changed
    mode: "controlled",
    validate: zodResolver(isQueueSubmission ? queueSchema(t) : schema(t)),
    initialValues: {
      serviceType: null as string | null,
      additionalInformation: "",
      firstName: "",
      surname: "",
      dob: null as Date | null,
      gender: null as string | null,
      phoneNumber: "",
      whatsappPhoneNumber: "",
      email: "",
      serviceDate: null as Date | null | string,
      serviceTime: "",
      slotId: "",
      doctorAssigned: doctor?.userId,
      language: i18n.language,
      sessions: [] as {
        session: number;
        date: Date | null;
        time: string;
        slotId?: string;
        dateType: string;
        isPredicted?: boolean;
      }[],
      additionalData: {} as Record<string, any>,
      files: [] as File[],
    },
  });

  const sessions = form.values.sessions || [];
  const sessionsCount = serviceType?.sessions ?? 1;
  const isMultiSession = sessionsCount > 1 && sessions.length > 0;

  const isFormComplete = isMultiSession
    ? sessions.every(s => s.date && s.time)
    : !!(form.values.serviceDate && form.values.serviceTime);

  const renderSlotLoadingError = (message?: string) => (
    <Stack align="center" gap="md" mt="2rem">
      <IconExclamationCircle size={48} color="var(--mantine-color-red-6)" />
      <Text fz={16} ta="center" maw={400} c="red">
        {message || t("error-loading-appointment-slots")}
      </Text>
      <Button
        type="button"
        variant="outline"
        onClick={() => window.location.reload()}
        leftSection={<IconRefresh size={16} />}>
        {t("try-again")}
      </Button>
    </Stack>
  );

  // useLogger("form", [form.values]);

  const handleFormSubmit = (values: any) => {
    import.meta.env.DEV && console.log("[DEV] ", values);
    if ((error as any)?.doctorProfileError) return;
    if (doctor?.userId) {
      trackDoctorFunnelEvent("booking_started", {
        doctorId: doctor.userId,
        profileId: doctor.id,
        doctorSlug: slug,
        bookingType: serviceType?.category,
        serviceId: serviceType?.value,
        language: i18n.language,
      });
    }
    openPhoneNumberVerificationModal();
  };

  const handleQueueSubmit = () => {
    if ((error as any)?.doctorProfileError) return;
    import.meta.env.DEV && console.log("[DEV] ", form.values);
    // Validate only the required fields for queue submission
    const queueValidation = queueSchema(t).safeParse(form.values);

    if (!queueValidation.success) {
      // Transform Zod errors to Mantine form error format
      const zodErrors = queueValidation.error.format();
      const mantineErrors: Record<string, string> = {};

      // Convert Zod error format to Mantine format
      Object.entries(zodErrors).forEach(([field, error]) => {
        if (error && typeof error === "object" && "_errors" in error) {
          mantineErrors[field] = error._errors[0];
        }
      });

      form.setErrors(mantineErrors);
      const firstErrorPath = Object.keys(mantineErrors)[0];
      form.getInputNode(firstErrorPath)?.focus();
      return;
    }

    setIsQueueSubmission(true);
    openPhoneNumberVerificationModal();
  };

  useEffect(() => {
    if (isAllowedToBook) {
      const formData = new FormData();
      appendPatientTimezone(formData);
      Object.entries(form.values).forEach(([key, value]) => {
        if (key === "additionalData" || key === "files") return; // Handled separately
        formData.append(key, value?.toString() ?? "");
      });
      formData.append("cost", serviceType?.originalPrice?.toString() ?? "");
      formData.append("serviceTypeLabel", serviceType?.label ?? "");

      // Use Mantine form state instead of DOM scraping
      formData.append("formData", JSON.stringify(form.values.additionalData));
      
      formData.append("duration", (serviceType?.duration ?? 0).toString());
      formData.append("promoCode", promoCode);
      formData.append("category", serviceType?.category ?? "");
      formData.append("discountPercent", discountPercent?.toString() || "0");
      formData.append(
        "location",
        latLng?.length ? latLng : locationCity + " " + locationStreet
      );

      // Add multi-session dates if available
      const sessions = form.values.sessions;
      if (sessions && sessions.length > 1) {
        const appointmentDates = sessions.map((s: any) => ({
          session: s.session,
          date: s.time, // s.time is the full ISO string from the Radio value
          ...(s.slotId ? { slot_id: s.slotId } : {}),
          is_predicted: !!s.isPredicted,
        }));
        formData.append("appointment_dates", JSON.stringify(appointmentDates));
        // Use the first session as the primary appointment_date
        formData.set("serviceTime", sessions[0].time);
        formData.set("serviceDate", sessions[0].date?.toISOString() ?? "");
        formData.set("slotId", sessions[0].slotId ?? "");
      }

      // Collect and append uploaded files from form state
      const files = form.values.files || [];
      files.forEach(file => {
        formData.append("files", file);
      });
      appendAccountAttribution(formData, slug);
      if (doctor?.userId) {
        appendDoctorAnalyticsAttribution(formData, {
          doctorId: doctor.userId,
          profileId: doctor.id,
          serviceId: serviceType?.value,
          bookingType: serviceType?.category,
        });
      }

      if (isQueueSubmission) {
        formData.append("isQueueSubmission", isQueueSubmission.toString());
        formData.append("timeZone", new Date().toISOString());
        doctorAsapActionFetcher.submit(formData, {
          method: "post",
          action: `/${i18n.language}/doctor/asap`,
          encType: "multipart/form-data",
        });
      } else {
        actionFetcher.submit(formData, {
          method: "post",
          encType: "multipart/form-data",
        });
      }
      setIsAllowedToBook(false);
      setIsQueueSubmission(false);
    }
  }, [
    isAllowedToBook,
    isQueueSubmission,
    form.values,
    serviceType,
    promoCode,
    discountPercent,
    latLng,
    locationCity,
    locationStreet,
    i18n.language,
    actionFetcher,
    doctorAsapActionFetcher,
  ]);

  useEffect(() => {
    if (
      serviceFormFetcher.state === "submitting" ||
      actionFetcher.state === "submitting" ||
      doctorAsapActionFetcher.state === "submitting"
    ) {
      setIsSubmitting(true);
    }
    // Only reset when both the form is no longer submitting
    // AND we've reached a new location (redirect completed)
  }, [serviceFormFetcher.state, actionFetcher.state]);

  useEffect(() => {
    const serviceTypeValue = form.values.serviceType;
    if (serviceTypeValue && doctor?.services) {
      const selectedService = doctor.services?.find(
        (item: any) => item.value === serviceTypeValue
      ) as ComboboxItemPrice;
      
      setServiceType(selectedService);
      
      if (mounted) {
        const previousServiceTypeValue = previousServiceTypeRef.current;
        const previousService = doctor.services.find(
          (item: any) => item.value === previousServiceTypeValue
        ) as ComboboxItemPrice | undefined;
        const expectedCount = Math.max(1, selectedService?.sessions ?? 1);
        const serviceChanged = previousServiceTypeValue !== serviceTypeValue;

        if (serviceChanged) {
          previousServiceTypeRef.current = serviceTypeValue;
          if (doctor?.userId) {
            trackDoctorFunnelEvent("service_selected", {
              doctorId: doctor.userId,
              profileId: doctor.id,
              doctorSlug: slug,
              bookingType: selectedService?.category,
              serviceId: selectedService?.value,
              language: i18n.language,
            });
          }

          const previousWasMultiSession =
            Math.max(1, previousService?.sessions ?? 1) > 1;
          const nextIsMultiSession = expectedCount > 1;
          const shouldResetSchedule =
            previousWasMultiSession || nextIsMultiSession;
          const hasSelectedSlot =
            Boolean(form.values.serviceTime) ||
            form.values.sessions.some(session => Boolean(session.time));

          if (shouldResetSchedule) {
            form.setFieldValue("serviceDate", null);
            form.setFieldValue("serviceTime", "");
            form.setFieldValue("slotId", "");
            setShowSlotResetNotice(hasSelectedSlot);
            slotTrackedRef.current = false;
          } else {
            setShowSlotResetNotice(false);
          }

          if (nextIsMultiSession) {
            const initialSessions = Array.from(
              { length: expectedCount },
              (_, i) => ({
                session: i + 1,
                date: null,
                time: "",
                slotId: "",
                dateType: "slot",
                isPredicted: false,
              })
            );

            form.setFieldValue("sessions", initialSessions);
          } else if (form.values.sessions.length) {
            form.setFieldValue("sessions", []);
          }
        }
      }
    } else if (!serviceTypeValue) {
      const previousService = doctor?.services?.find(
        (item: any) => item.value === previousServiceTypeRef.current
      ) as ComboboxItemPrice | undefined;
      const previousWasMultiSession =
        Math.max(1, previousService?.sessions ?? 1) > 1;

      if (mounted && previousWasMultiSession) {
        const hadSelectedSession = form.values.sessions.some(session =>
          Boolean(session.time)
        );
        form.setFieldValue("serviceDate", null);
        form.setFieldValue("serviceTime", "");
        form.setFieldValue("slotId", "");
        form.setFieldValue("sessions", []);
        setShowSlotResetNotice(hadSelectedSession);
        slotTrackedRef.current = false;
      }
      previousServiceTypeRef.current = null;
      setServiceType(null);
    }
  }, [form.values.serviceType, doctor?.services, mounted]);

  useEffect(() => {
    const hasSelectedSlot =
      Boolean(form.values.serviceTime) ||
      form.values.sessions.some(session => Boolean(session.time));
    if (showSlotResetNotice && hasSelectedSlot) {
      setShowSlotResetNotice(false);
    }
  }, [
    form.values.serviceTime,
    form.values.sessions,
    showSlotResetNotice,
  ]);

  useEffect(() => {
    const hasSelectedSlot =
      Boolean(form.values.serviceTime) ||
      form.values.sessions.some(session => Boolean(session.time));
    if (!hasSelectedSlot || slotTrackedRef.current || !doctor?.userId) return;
    slotTrackedRef.current = true;
    trackDoctorFunnelEvent("slot_selected", {
      doctorId: doctor.userId,
      profileId: doctor.id,
      doctorSlug: slug,
      bookingType: serviceType?.category,
      serviceId: serviceType?.value,
      language: i18n.language,
    });
  }, [
    doctor?.id,
    doctor?.userId,
    form.values.serviceTime,
    form.values.sessions,
    i18n.language,
    serviceType?.category,
    slug,
  ]);

  useEffect(() => {
    if (serviceType?.value) {
      serviceFormFetcher.load(
        `/api/service-form?serviceId=${serviceType.value}`
      );
    }
  }, [serviceType?.value]);

  useEffect(() => {
    if (serviceFormFetcher.data) {
      setFormFields(serviceFormFetcher.data?.data ?? []);
    }
  }, [serviceFormFetcher.data]);

  useEffect(() => {
    if (promoCodeFetcher.data?.data && serviceType?.value) {
      const services = promoCodeFetcher.data.data?.services;
      const isValid = promoCodeFetcher.data.data?.isValid;
      // console.log(isValid, services);
      if (services?.includes(serviceType.value)) {
        // console.log(promoCodeFetcher.data.data);
        setIsPromoCodeValid(promoCodeFetcher.data?.data.isValid.toString());
        setDiscountPercent(promoCodeFetcher.data?.data.discountPercent);
      } else if (!services?.length && isValid) {
        // Repeating this block of code intentionally - Apply promocode to all services
        setIsPromoCodeValid(promoCodeFetcher.data?.data.isValid.toString());
        setDiscountPercent(promoCodeFetcher.data?.data.discountPercent);
      } else {
        setIsPromoCodeValid("false");
      }
    }
  }, [promoCodeFetcher.data, serviceType?.value]);

  useEffect(() => {
    // console.log(actionFetcher.data?.error);
    if ((actionFetcher.data as any)?.error || (doctorAsapActionFetcher.data as any)?.error) {
      resetIsSubmitting();
    }
  }, [actionFetcher.data]);

  return (
    <div className={classes.section}>
      {physicianSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(physicianSchema) }}
        />
      )}
      <div className={utilityClasses["small-container"]}>
        <Form
          ref={formRef}
          method="post"
          className={sharedClasses.body}
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              import.meta.env.DEV && console.log("[DEV] form errors:", errors);
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <Group justify="space-between">
            <Button
              component={Link}
              to={`/${i18n.language}`}
              reloadDocument
              type="button"
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={16} />}>
              {t("go-see-specialists")}
            </Button>
            <Button
              onClick={() =>
                shareOrCopyLink(setCopied, {
                  title: `${doctor?.name ?? PAGE_TITLE} | ${PAGE_TITLE}`,
                  text: doctor?.name
                    ? `Book an appointment with ${doctor.name} on La Ruche Health.`
                    : `Book an appointment on La Ruche Health.`,
                })
              }
              variant="subtle"
              size="compact-sm"
              rightSection={
                copied ? <IconCopyCheck size={16} /> : <IconShare size={16} />
              }>
              {copied ? t("copied-link") : t("copy-link")}
            </Button>
          </Group>

          {renderLoadingErrorData({
            isLoading: false,
            loadingComponent: (
              <Center>
                <Loader size={30} />
              </Center>
            ),
            isError: !!(error as any)?.doctorProfileError,
            errorComponent: (
              <Alert
                mt="1rem"
                variant="light"
                color={"red"}
                radius={"sm"}
                icon={<IconExclamationCircle />}
                title={
                  <Text c={"red"} fw={500} fz={14} lh={1.5}>
                    {t("oops")}
                  </Text>
                }>
                <Text fz={14} lh={1.5} c={"red"}>
                  {/* {doctor?.data?.error?.status === 404
                    ? t("couldnt-find-doctor")
                    : t("error-retrieving-doctor")} */}
                  {t("error-retrieving-doctor")}
                </Text>
              </Alert>
            ),
            dataLength: Object.keys(doctor ?? {}).length,
            emptyDataComponent: <></>,
            isSuccess: ok?.doctorProfile || !!Object.keys(doctor ?? {}).length,
            dataComponent: (
              <div className={classes["doctor-profile"]}>
                
                <div className={classes["img-wrapper"]}>
                  <Image
                    src={doctor?.image ?? placeholderPerson}
                    h={{ base: 100, sm: 120 }}
                    w={{ base: 100, sm: 120 }}
                    alt={doctor?.name}
                    radius={"100vmax"}
                  />
                </div>
                <Text component="h1" fz={20} fw={600} ta={"center"} mt={"1rem"} mb={0}>
                  {doctor?.name}
                </Text>

                <Group gap={5} mt="0.75rem" justify="center">
                  <Group gap={3}>
                    <IconBriefcase size={16} color="var(--clr-gray-500)" />
                    <Text fz={{ base: 13, sm: 14 }} span inherit c="dimmed">
                      {doctor?.specialization}
                    </Text>
                  </Group>

                  {doctor?.consultationModes?.includes("homecare") && (
                    <div className={doctorCardClasses["icon-wrapper"]}>
                      <IconHome size={16} />
                    </div>
                  )}

                  {doctor?.consultationModes?.includes("teleconsultation") && (
                    <div className={doctorCardClasses["icon-wrapper"]}>
                      <IconVideo size={16} />
                    </div>
                  )}
                </Group>

                <Group
                  mt={"0.75rem"}
                  gap={"md"}
                  justify="center"
                  // mx="auto"
                  // w="fit-content"
                  align="center"
                  style={{ rowGap: "0.5rem" }}>
                  {doctor?.licenseNumber && (
                    <Text fz={14} c="dimmed">
                      {doctor?.licenseNumber}
                    </Text>
                  )}
                  <Group gap={3} justify="center">
                    <IconMapPin size={16} color="var(--clr-gray-500)" />
                    <Text fz={14} span inherit c="dimmed" truncate maw={200}>
                      {doctor?.location}
                    </Text>
                  </Group>
                </Group>

                <Group
                  gap={5}
                  mt={"1rem"}
                  justify="center"
                  w="min(100%, 60ch)"
                  mx={"auto"}>
                  {doctorSpecialties.map((item: any, index: number) => {
                    const specialtyPath = getSpecialtyLandingPagePath(
                      item.label,
                      i18n.language
                    );

                    return specialtyPath ? (
                      <Badge
                        key={index}
                        component={Link}
                        to={specialtyPath}
                        variant="outline"
                        tt={"capitalize"}
                        fw={500}>
                        {item.label}
                      </Badge>
                    ) : (
                      <Badge
                        key={index}
                        variant="outline"
                        tt={"capitalize"}
                        fw={500}>
                        {item.label}
                      </Badge>
                    );
                  })}
                </Group>

                <Text
                  c="dimmed"
                  fz={14}
                  ta="center"
                  mt={"1rem"}
                  maw={"70ch"}
                  mx="auto">
                  {doctor?.briefDescription}
                </Text>
                <Text
                  c="dimmed"
                  fz={14}
                  ta="center"
                  mt={"0.5rem"}
                  maw={"70ch"}
                  mx="auto"
                  tt={"capitalize"}>
                  {t("languages")}:&nbsp;
                  {doctor?.languages
                    ?.map(lang => t(lang as TranslationKey))
                    .join(", ")}
                  .
                </Text>
                {shouldShowRating ? (
                  <Group gap={8} justify="center" mt="1.5rem">
                    <Text fz={14} fw={500} c="dimmed">
                      {t("rating")}: {ratingSummary?.average?.toFixed(1)}
                    </Text>
                    <Group gap={6}>
                      {Array.from({ length: 5 }).map((_, index) => (
                          <IconStarFilled
                            key={index}
                            size={18}
                            color={
                              index < roundedRating
                                ? "var(--mantine-color-yellow-5)"
                              : "var(--mantine-color-gray-3)"
                          }
                        />
                      ))}
                    </Group>
                    <Text fz={14} c="dimmed">
                      ({t("x-reviews", { amount: ratingSummary?.count ?? 0 })})
                    </Text>
                  </Group>
                ) : null}
                <Center mt={"0.75rem"}>
                  <Stack gap="1rem" align="center">
                    <Badge py={10} bg="hsla(180, 50%, 95%, 1)">
                      <Group gap={5}>
                        <IconTrophyFilled
                          size={16}
                          color="var(--mantine-color-primary-6)"
                        />
                        <Text c="primary.6" fz={12} fw={500} tt="capitalize">
                          {t("x-years-of-experience", {
                            amount: doctor?.yearsOfExperience,
                          })}
                        </Text>
                      </Group>
                    </Badge>
                    <Button
                      component={Link}
                      reloadDocument
                      to={`masterclass`}
                      variant="light"
                      color="accent"
                      style={{ borderRadius: "100vmax" }}
                      leftSection={<IconBook size={16} stroke={1.5} />}>
                      {t("join-my-masterclass")}
                    </Button>
                  </Stack>
                </Center>
              </div>
            ),
          })}

          <Title
            order={2}
            fw={600}
            fz={20}
            mt={"3rem"}
            ta={"center"}
            className={sharedClasses["body-heading"]}>
            {t("fill-form-to-book-appointment")}
          </Title>

          <Alert
            mt="1rem"
            variant="light"
            color="blue"
            radius="sm"
            icon={<IconInfoCircle size={24} />}
            title={<Text fw={600} fz={15}>{t("support-banner-title")}</Text>}
          >
            <Stack gap={5} mt={5}>
              <Text fz={14}>{t("support-banner-desc")}</Text>
              <Group gap="md">
                <Group gap={5}>
                  <IconPhone size={16} />
                  <Text fz={14} fw={500}>+225 05 04 78 36 82</Text>
                </Group>
                <Group gap={5}>
                  <IconMail size={16} />
                  <Text fz={14} fw={500}>rendezvous@laruche.health</Text>
                </Group>
              </Group>
            </Stack>
          </Alert>

          <ConsultationServiceSelect
            disabled={!ok?.doctorProfile}
            rightSection={
              (error as any)?.doctorProfileData ? (
                <IconExclamationCircle color="red" size={20} />
              ) : null
            }
            mt={"3rem"}
            withAsterisk
            label={t("service-type")}
            {...form.getInputProps("serviceType")}
            key={form.key("serviceType")}
            data={doctor?.services?.map((item: any) => ({
              value: item.value,
              label: item.label,
              price: item.price,
              duration: item.duration,
              category: item.category,
            }))}
          />

          <Title order={2} fw={500} fz={18} mt={"3rem"}>
            {t("date-and-time")}
          </Title>

          {showSlotResetNotice && (
            <Alert
              mt="sm"
              variant="light"
              color="yellow"
              radius="sm"
              icon={<IconInfoCircle size={20} />}>
              <Text fz={14}>{t("appointment-times-reset-service-change")}</Text>
            </Alert>
          )}

          <ClientOnly>
                <Suspense
                  fallback={
                    <SlotLoadingFallback
                      loadingText={t("loading-available-times")}
                      timeoutText={t("error-loading-appointment-slots")}
                      retryText={t("try-again")}
                    />
                  }
                >
                  <Await
                    resolve={data.slotsPromise}
                    errorElement={renderSlotLoadingError()}>
                    {(slotsRes) => {
                  // Handle error case with retry option
                  if (!slotsRes?.ok && slotsRes?.error) {
                    return renderSlotLoadingError(slotsRes.error.message);
                  }

                  const slotsData = slotsRes?.data?.map((item: any) => ({
                    ...item,
                    startTime: new Date(item.startTime),
                    endTime: new Date(item.endTime),
                  })) ?? [];

                  const sessions = form.values.sessions || [];
                  const sessionsCount = serviceType?.sessions ?? 1;
                  const showMultiSessionUI = sessionsCount > 1 && sessions.length > 0;

                  return (
                    <>
                      {showMultiSessionUI ? (
                        <MultiSessionScheduler
                          form={form}
                          slotsData={slotsData}
                          serviceType={serviceType}
                        />
                      ) : (
                        <>
                          {slotsData?.length ? (
                            <>
                              <Radio.Group
                                label={t("service-date")}
                                withAsterisk
                                mt={"1rem"}
                                value={dateType}
                                onChange={setDateType}>
                                <Stack>
                                  <Radio label={t("select-date-slot")} value={"slot"} />
                                  {dateType === "slot" ? (
                                    slotsData?.length ? (
                                      <Radio.Group
                                        {...form.getInputProps("serviceDate")}
                                        onChange={value => {
                                          form.setFieldValue("serviceDate", value);
                                          form.setFieldValue("serviceTime", "");
                                          form.setFieldValue("slotId", "");
                                        }}
                                        key={form.key("serviceDate")}>
                                        <Group gap={"xs"}>
                                          {Array.from(
                                            new Set(
                                              slotsData
                                                ?.map((item: any) =>
                                                  dayjs(item.startTime)
                                                    .set("hours", 0)
                                                    .set("minutes", 0)
                                                    .set("seconds", 0)
                                                    .set("milliseconds", 0)
                                                    .toISOString()
                                                )
                                                .sort(
                                                  (a: string, b: string) =>
                                                    new Date(a).getTime() -
                                                    new Date(b).getTime()
                                                )
                                            )
                                          )
                                            .slice(0, 5)
                                            .map((item: any, index: number) => (
                                              <DateSlot key={index} date={new Date(item)} />
                                            ))}
                                        </Group>
                                      </Radio.Group>
                                    ) : (
                                      <Text fz={14} c="dimmed">
                                        {t("no-available-slots")}
                                      </Text>
                                    )
                                  ) : null}
                                  <Radio label={t("select-date-manually")} value={"manual"} />
                                  {dateType === "manual" && (
                                    <DatePickerInput
                                      w={"100%"}
                                      maw={350}
                                      withAsterisk
                                      placeholder={t("select-service-date")}
                                      leftSection={<IconCalendar size={16} stroke={1.5} />}
                                      // {...form.getInputProps("serviceDate")}
                                      value={
                                        typeof form.values.serviceDate === "string"
                                          ? new Date(form.values.serviceDate as string)
                                          : (form.values.serviceDate as Date)
                                      }
                                      onChange={value => {
                                        form.setFieldValue("serviceDate", value);
                                        form.setFieldValue("serviceTime", "");
                                        form.setFieldValue("slotId", "");
                                      }}
                                      key={form.key("serviceDate")}
                                      minDate={new Date()}
                                      // maxDate={new Date(new Date().setDate(new Date().getDate() + 21))}
                                      excludeDate={(date: Date) => {
                                        // Go through each slot and compare its startTime with the given date
                                        const isSlotAvailable = slotsData?.some((slot: any) => {
                                          const slotDate = new Date(
                                            slot.startTime
                                          ).toDateString(); // Convert startTime to a Date object
                                          return slotDate === date.toDateString(); // Compare only the date part
                                        });

                                        return !isSlotAvailable; // Return true if no slots found for the given date
                                      }}
                                    />
                                  )}
                                </Stack>
                              </Radio.Group>

                              <Radio.Group
                                label={t("service-time")}
                                withAsterisk
                                mt={"1.5rem"}
                                {...form.getInputProps("serviceTime")}
                                onChange={value => {
                                  const selectedSlot = getSlotsForDay(
                                    slotsData,
                                    form.values.serviceDate as Date
                                  )?.find(slot => slot.startTime === value);
                                  form.setFieldValue("serviceTime", value);
                                  form.setFieldValue("slotId", selectedSlot?.id ?? "");
                                }}
                                key={form.key("serviceTime")}>
                                {!form.values.serviceDate ? (
                                  <Text fz={14} c="dimmed" fs={"italic"}>
                                    {t("select-date-for-options")}
                                  </Text>
                                ) : (
                                  <ServiceTimeOptions
                                    serviceDate={form.values.serviceDate as Date}
                                    slots={getSlotsForDay(
                                      slotsData,
                                      form.values.serviceDate as Date
                                    )}
                                  />
                                )}
                              </Radio.Group>
                            </>
                          ) : enableQueue ? (
                            <Stack gap="1rem" mt="1rem">
                              <Text fz={14} c="dimmed">
                                {t("no-available-slots")} {t("join-queue-description")}
                              </Text>
                              <Button
                                type="button"
                                variant="outline"
                                w={"fit-content"}
                                onClick={handleQueueSubmit}
                                disabled={!serviceType?.value || !!(error as any)?.doctorProfileError}
                                loading={isSubmitting}>
                                {t("join-queue")}
                              </Button>
                            </Stack>
                          ) : (
                            <Text fz={14} c="dimmed" mt="1rem">
                              {t("no-available-slots")}
                            </Text>
                          )}
                        </>
                      )}
                    </>
                  );
                    }}
                  </Await>
                </Suspense>
          </ClientOnly>

          {serviceType?.category === "homecare" ? (
            <>
              <Title order={2} fw={500} fz={18} mt={"3rem"}>
                {t("location-details")}
              </Title>
              <Radio.Group
                withAsterisk
                mt="1rem"
                label={t("where-are-you-located")}
                value={locationOption}
                onChange={setLocationOption}>
                <Stack mt="0.5rem">
                  <Radio label={t("search-for-your-location")} value="search" />
                  {locationOption === "search" ? (
                    <>
                      <VisGoogleMap setLatLng={setLatLng} />
                    </>
                  ) : null}
                  <Radio
                    label={t("enter-location-manually")}
                    value="enter"
                    mt={"1rem"}
                  />
                  {locationOption === "enter" ? (
                    <Stack>
                      <TextInput
                        w={"calc(50% - 1rem)"}
                        label={t("city")}
                        placeholder={t("eg", { example: "Abidjan" })}
                        value={locationCity}
                        onChange={e => setLocationCity(e.target.value)}
                      />
                      <TextInput
                        w={"calc(50% - 1rem)"}
                        label={t("street-address")}
                        placeholder={t("eg", { example: "123 Avenue" })}
                        value={locationStreet}
                        onChange={e => setLocationStreet(e.target.value)}
                      />
                    </Stack>
                  ) : null}
                </Stack>
              </Radio.Group>
            </>
          ) : null}

          <Box className={classes["data-protection-card"]} mt="3rem">
            <IconLock size={26} stroke={2.5} aria-hidden="true" />
            <Text fz={14} fw={400} lh={1.5}>
              {t("data-confidentiality-description")}
            </Text>
          </Box>

          <Title order={2} fw={500} fz={18} mt={"3rem"}>
            {t("personal-details")}
          </Title>

          <Group
            justify="space-between"
            align="start"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("first-name")}
              placeholder={t("eg", { example: "John" })}
              {...form.getInputProps("firstName")}
              key={form.key("firstName")}
              withAsterisk
            />
            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("surname")}
              placeholder={t("eg", { example: "Doe" })}
              {...form.getInputProps("surname")}
              key={form.key("surname")}
              withAsterisk
            />
          </Group>
          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            {/* <DatePickerInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder={t("select-dob")}
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            /> */}
            <DateInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder="DD/MM/YYYY"
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            />
            <Select
              w={{ base: "100%", sm: "48%" }}
              label={t("gender")}
              placeholder={t("select-gender")}
              data={getGenderValues(t)}
              {...form.getInputProps("gender")}
              key={form.key("gender")}
              withAsterisk
            />
          </Group>
          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            <Stack gap="0.25rem" w={"48%"}>
              {noWhatsappNumber ? (
                <Input.Wrapper
                  label={t("phone-number")}
                  w={{ base: "100%" }}
                  withAsterisk>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.phoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.phoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("phoneNumber")}
                    key={form.key("phoneNumber")}
                  />
                  <Input.Error mt={5}>{form.errors.phoneNumber}</Input.Error>
                </Input.Wrapper>
              ) : (
                <Input.Wrapper
                  label={t("whatsapp-phone-number")}
                  description={t("whatsapp-number-reason")}
                  withAsterisk
                  w={{ base: "100%" }}>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.whatsappPhoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.whatsappPhoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("whatsappPhoneNumber")}
                    key={form.key("whatsappPhoneNumber")}
                  />
                  <Input.Error mt={5}>
                    {form.errors.whatsappPhoneNumber}
                  </Input.Error>
                </Input.Wrapper>
              )}

              <Checkbox
                label={t("no-whatsapp-number")}
                size="xs"
                checked={noWhatsappNumber}
                onChange={e => {
                  const val = e.currentTarget.checked;
                  setNoWhatsappNumber(val);
                  if (val) {
                    form.setFieldValue("whatsappPhoneNumber", "");
                  } else {
                    form.setFieldValue("phoneNumber", "");
                  }
                }}
              />
            </Stack>

            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("email")}
              type="email"
              leftSection={<IconAt size={16} stroke={1.5} />}
              placeholder={t("eg", { example: "johndoe@example.com" })}
              {...form.getInputProps("email")}
              key={form.key("email")}
              ref={emailRef}
            />
          </Group>

          <Textarea
            withAsterisk
            label={t("appointment-purpose")}
            mt={"3rem"}
            placeholder={t("appointment-purpose-description")}
            rows={7}
            {...form.getInputProps("additionalInformation")}
            key={form.key("additionalInformation")}
          />

          {/* Additional form fields start here --------------------------- */}
          {formFields.length ? (
            <>
              <Title order={2} fw={500} fz={18} mt={"3rem"}>
                {t("extra-info")}
              </Title>
              <DynamicFormFields fields={formFields} form={form} t={t} />
            </>
          ) : null}
          {/* Additional form fields end here ---------------------------- */}

          <Group
            align="end"
            w={{ base: "100%", sm: "calc(50% - 1rem)" }}
            mt={"3rem"}
            gap={5}>
            <TextInput
              disabled={isPromoCodeValid === "true" || !serviceType?.value}
              label={t("promo-code")}
              description={
                !serviceType?.value
                  ? t("select-service-before-promo-code")
                  : null
              }
              flex={1}
              placeholder={t("eg", { example: "ABCD123" })}
              rightSection={
                <IconRosetteDiscount size={20} stroke={1.5} color="green" />
              }
              value={promoCode}
              onChange={e => setPromoCode(e.target.value as string)}
            />
            <Button
              disabled={
                !promoCode || isPromoCodeValid === "true" || !serviceType?.value
              }
              loading={promoCodeFetcher.state === "submitting"}
              size="xs"
              variant="outline"
              type="button"
              h={36}
              onClick={() => {
                if (!promoCode || !serviceType?.value) return;
                promoCodeFetcher.submit(null, {
                  method: "post",
                  action: `/api/validate-promo-code/${promoCode}`,
                });
              }}>
              {t("apply")}
            </Button>
          </Group>

          {isPromoCodeValid === "true" ? (
            <Alert
              mt="1rem"
              variant="light"
              color={"green"}
              radius={"sm"}
              icon={<IconDiscountCheckFilled />}
              // withCloseButton
              onClose={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"green"}>
                <Trans
                  i18nKey="promo-code-discount-x"
                  values={{ discountPercent }}
                  components={{ bold: <Text span inherit fw={600} /> }}
                />
              </Text>
            </Alert>
          ) : null}

          {isPromoCodeValid === "false" ? (
            <Alert
              withCloseButton
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              onClick={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"red"}>
                {t("promo-code-invalid")}
              </Text>
            </Alert>
          ) : null}

          {actionFetcher.data && (
            <Alert
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              title={
                <Text c={"red"} fw={500} fz={14} lh={1.5}>
                  {t("oops")}
                </Text>
              }>
              <Text fz={14} lh={1.5} c={"red"}>
                {t("create-appointment-error")}
              </Text>
            </Alert>
          )}

          <PhoneNumberVerificationModal
            whatsappPhoneNumber={
              form.values.whatsappPhoneNumber.trim().length > 3
                ? form.values.whatsappPhoneNumber
                : ""
            }
            phoneNumber={
              form.values.phoneNumber.trim().length > 3
                ? form.values.phoneNumber
                : ""
            }
            opened={phoneNumberVerificationModalOpened}
            close={closePhoneNumberVerificationModal}
            setIsAllowedToBook={setIsAllowedToBook}
            timeLeft={timeLeft}
            isComplete={isComplete}
            resetTimer={resetTimer}
          />

          <Button
            type="submit"
            radius={"xl"}
            w={"100%"}
            mt={"3rem"}
            disabled={!!(error as any)?.doctorProfileError || !isFormComplete}
            loading={isSubmitting}>
            {t("confirm-appointment")}
          </Button>
          <Text fz={12} c="dimmed" fs={"italic"} mt={"0.5rem"} ta="center">
            <Trans i18nKey="agree-to-privacy-policy">
              By confirming your appointment, you agree to our{" "}
              <Anchor
                component={Link}
                target="_blank"
                to="https://docs.google.com/document/d/1amefCfTGw-erveo2ILEYqe_Ppvb0VXYDjjq0lE32f1A/edit?usp=sharing"
                inherit>
                privacy policy
              </Anchor>
              .
            </Trans>
          </Text>
        </Form>
        <KikoDoctorProfileAssistant
          disabled={!!(error as any)?.doctorProfileError}
          language={i18n.language}
          doctor={{
            doctorId: doctor?.userId ?? "",
            profileId: doctor?.id,
            slug,
            name: doctor?.name,
            specialization: doctor?.specialization,
            specialties: doctorSpecialties,
            location: doctor?.location,
            consultationModes: doctor?.consultationModes,
            services: doctor?.services,
          }}
        />
      </div>
    </div>
  );
}
export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  // const t = await i18nextServer.getFixedT(request);
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;
  const slug = params.slug ?? "";
  const topLevelRoutes = new Set(["health-library", "lab", "specialites"]);

  if (topLevelRoutes.has(slug)) {
    throw redirect(`/${locale}/${slug}`);
  }

  const currencyContext = (await resolveCurrencyContext(request, true)) ?? {
    currency: "XOF",
    fxRate: 1,
    country: "ci",
  };
  const { currency, fxRate } = currencyContext;

  // * NB: Translation doesn't work for meta tags after deployment
  let titleText =
    locale === "fr"
      ? `Prenez rendez-vous dès maintenant avec ${getNameFromSlug(slug)} !`
      : `Book your appointment now with ${getNameFromSlug(slug)}!`;

  // const title = `${t("book-appointment-now-with-x")} • ${PAGE_TITLE}`;
  let title = `${titleText} • ${PAGE_TITLE}`;
  let descText =
    locale === "fr"
      ? `Prenez rendez-vous avec ${getNameFromSlug(
          params?.slug as string
        )} pour les soins à domicile ou les consultations à distance à Abidjan.`
      : `Book an appointment with ${getNameFromSlug(
          params?.slug as string
        )} for homecare or teleconsultation in Abidjan.`;

  try {
    const data1 = await getDoctorProfile(params.slug ?? "");

    let slotsPromise = Promise.resolve({ ok: false, data: null, error: "Not requested" } as any);
    let doctorSpecialtiesData: any[] = [];

    if (!data1?.ok && data1?.error?.status === 404) {
      throw new Response("Doctor not found", { status: 404 });
    }

    if (data1?.ok && data1?.data?.id) {
      const doctorId = data1?.data?.id as string;
      const userId = data1?.data?.userId as string;

      slotsPromise = resolveSlotsWithTimeout(getAppointmentSlots(userId));
      const doctorSpecialtiesRes = await getDoctorSpecialties(doctorId);
      doctorSpecialtiesData = doctorSpecialtiesRes?.data ?? [];
    }

    const ok = {
      doctorProfile: data1?.ok,
    };

    const errors: Record<string, any> = {};
    if (!data1?.ok) {
      errors.doctorProfileError = data1?.error;
    }

    const services = data1.data?.services?.map((s: any) => s.label).join(", ");
    const consultationModes = data1.data?.consultationModes?.join(", ");

    if (data1.data?.services) {
      data1.data.services = data1.data.services.map((service: any) => ({
        ...service,
        originalPrice: service?.price ?? 0, // Store original XOF price
        price: (service?.price ?? 0) * fxRate, // Display converted price
      }));
    }

    titleText = `${data1.data?.name}, ${data1.data?.specialization}`;

    title = `${titleText} | ${PAGE_TITLE}`;

    descText =
      locale === "fr"
        ? `Prenez rendez-vous en ligne avec ${data1.data?.name}, ${data1.data?.specialization} à ${data1.data?.location}. Rapide, fiable et accessible.`
        : `Book an appointment online with ${data1.data?.name}, ${data1.data?.specialization} in ${data1.data?.location}. Fast, reliable and accessible.`;

    const physicianSchema = data1.data
      ? getDoctorStructuredData({
          doctor: data1.data,
          origin,
          locale,
          slug,
          services: services ?? "",
        })
      : null;

    // If the primary API call failed, return early with 200 so the component can render the error UI
    if (Object.keys(errors).length > 0) {
      return defer(
        {
          ok,
          metaTitle: title,
          metaDoctorName: data1.data?.name ?? getNameFromSlug(slug),
          metaDescription: descText,
          metaLocation: data1.data?.location,
          metaYearsOfExperience: data1.data?.yearsOfExperience,
          metaSpecialization: data1.data?.specialization,
          metaServices: services,
          metaLocale: locale,
          metaOrigin: origin,
          slug,
          image: data1?.data?.image,
          currency,
          enableQueue: process.env.ENABLE_QUEUE_FEATURE === "true",
          physicianSchema: null,
          data: {
            doctorProfileData: data1?.data || null,
            slotsPromise,
            doctorSpecialtiesData,
          },
          error: errors,
        }
      );
    }

    // Return deferred data if successful
    return defer(
      {
        ok,
        metaTitle: title,
        metaDoctorName: data1.data?.name ?? getNameFromSlug(slug),
        metaDescription: descText,
        metaLocation: data1.data?.location,
        metaYearsOfExperience: data1.data?.yearsOfExperience,
        metaSpecialization: data1.data?.specialization,
        metaServices: services,
        metaLocale: locale,
        metaOrigin: origin,
        image: data1?.data?.image,
        slug,
        currency,
        enableQueue: process.env.ENABLE_QUEUE_FEATURE === "true",
        physicianSchema,
        data: {
          doctorProfileData: data1?.data,
          slotsPromise,
          doctorSpecialtiesData,
        },
        error: null,
      }
    );
  } catch (error: any) {
    if (error instanceof Response) {
      throw error;
    }

    // Handle any unexpected errors outside of API responses
    console.error("Unexpected error", error);
    return defer(
      {
        ok: {
          doctorProfile: false,
        },
        metaTitle: title,
        metaDoctorName: getNameFromSlug(slug),
        metaDescription: descText,
        metaLocation: "",
        metaYearsOfExperience: "",
        metaSpecialization: "",
        metaServices: "",
        metaLocale: locale,
        metaOrigin: origin,
        currency,
        enableQueue: process.env.ENABLE_QUEUE_FEATURE === "true",
        image: null,
        data: {
          doctorProfileData: null,
          slotsPromise: Promise.resolve(null),
          doctorSpecialtiesData: [],
        },
        slug,
        physicianSchema: null,
        error: "Unexpected error occurred",
      }
    );
  }};

// Booking Success Info cookie
const cookie = createCookie(`_${LARUCHE_IDENTIFIER}_bsi`, {
  maxAge: 60 * 60 * 24, // 1 day
  path: "/", // Path where the cookie is accessible - Entire domain
  secure: process.env.NODE_ENV === "production",
  httpOnly: true,
  sameSite: "lax",
  secrets: [process.env.COOKIE_SECRET as string],
});

export const action = async ({ request, params }: ActionFunctionArgs) => {
  try {
    const formData = await request.formData();

    // Extract files from formData
    const files = formData.getAll("files") as File[];

    const newForm = new FormData();
    newForm.append("category", formData.get("category")?.toString() ?? "");
    newForm.append("status", "pending");
    newForm.append(
      "doctor_assigned",
      formData.get("doctorAssigned")?.toString() ?? ""
    );
    newForm.append("is_follow_up", "false");
    newForm.append("priority", "scheduled");
    newForm.append("medium", "website");
    newForm.append("language", formData.get("language")?.toString() ?? "");
    if (formData.get("patient_timezone")) {
      newForm.append("patient_timezone", formData.get("patient_timezone")!.toString());
    }
    // serviceTime is now an ISO string, so we use it directly
    const serviceTimeISO = formData.get("serviceTime")?.toString() ?? "";
    newForm.append("appointment_date", serviceTimeISO);
    const slotId = formData.get("slotId")?.toString();
    if (slotId) {
      newForm.append("slot_id", slotId);
    }

    if (formData.get("appointment_dates")) {
      newForm.append(
        "appointment_dates",
        formData.get("appointment_dates") as string
      );
    }

    newForm.append("form_data", formData.get("formData")?.toString() ?? "");
    newForm.append("promo_code", formData.get("promoCode")?.toString() ?? "");
    newForm.append(
      "timezone_offset_minutes",
      new Date(formData.get("serviceDate")?.toString() || serviceTimeISO)
        .getTimezoneOffset()
        .toString()
    );
    [
      "attribution_token",
      "attribution_share_id",
      "attribution_channel",
      "attribution_session_id",
      "attribution_visitor_id",
      "analytics_first_touch",
      "analytics_last_touch",
      "analytics_consent",
      "marketing_consent",
      "analytics_journey_id",
      "analytics_origin_platform",
      "analytics_entry_point",
      "analytics_profile_id",
      "analytics_service_id",
    ].forEach(field => {
      const value = formData.get(field);
      if (value) newForm.append(field, value);
    });

    // Append uploaded files to the new form
    files.forEach(file => {
      newForm.append("files", file);
    });

    const whatsappPhoneNumber = cleanPhoneNumber(
      formData.get("whatsappPhoneNumber")?.toString()
    );

    const details = {
      phone_number:
        whatsappPhoneNumber ||
        cleanPhoneNumber(formData.get("phoneNumber")?.toString()),
      whatsapp_phone_number: whatsappPhoneNumber,
      first_name: formData.get("firstName"),
      surname: formData.get("surname"),
      dob: dayjs(formData.get("dob") as string).format("DD/MM/YYYY"),
      gender: formData.get("gender"),
      email: formData.get("email"),
      appointment_type: formData.get("category"),
      location: formData.get("location"),
    } as any;

    if (formData.get("category")?.toString() === "teleconsultation") {
      details.t_service_date = serviceTimeISO;
      details.t_service_type = formData.get("serviceTypeLabel");
      details.t_service_type_id = formData.get("serviceType");
      details.t_additional_health_info = formData.get("additionalInformation");
      details.t_cost = formData.get("cost");
    } else {
      details.service_date = serviceTimeISO;
      details.service_type = formData.get("serviceTypeLabel");
      details.service_type_id = formData.get("serviceType");
      details.additional_health_info = formData.get("additionalInformation");
      details.cost = formData.get("cost");
    }

    newForm.append("details", JSON.stringify(details));

    const cookiePayload = {
      serviceType: formData.get("serviceTypeLabel"),
      serviceTime: formData.get("serviceTime"),
      serviceDate: formData.get("serviceDate"),
      price: formData.get("cost"),
      doctor: getNameFromSlug(params.slug as string),
      duration: formData.get("duration"),
      discountPercent: formData.get("discountPercent"),
      email: formData.get("email"),
      phoneNumber:
        whatsappPhoneNumber ||
        cleanPhoneNumber(formData.get("phoneNumber")?.toString()),
      promoCode: +formData.get("discountPercent")!
        ? formData.get("promoCode")
        : "", // * If there is no discount don't use promo code
    } as any;

    console.log("Data to submit:", newForm);
    const res = await api.post(`appointments/multipart`, newForm, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    cookiePayload.apptId = res.data._id;
    const cookieHeader = await cookie.serialize(cookiePayload);

    return redirect(
      `${params?.lang ? `/${params.lang}` : ""}/payment?apptId=${res.data._id}`,
      {
        headers: {
          "Set-Cookie": cookieHeader,
        },
      }
    );
  } catch (error: any) {
    console.error("Error creating appointment:", error);

    // You can check if the error is an AxiosError
    if (isAxiosError(error)) {
      if (error.response) {
        // Handle the response error
        return remixData(
          { error: error.response.data?.detail ?? "An error occurred." },
          { status: error.response.status }
        );
      }
      // Handle other types of Axios errors (like network errors)
      return remixData({ error: "Network error." }, { status: 500 });
    }

    // Handle generic errors
    return remixData({ error: "Failed to create appointment." }, { status: 500 });
  }
};

export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) {
    // todo: Replace with appropriate component
    return <NotFoundPage />;
  }
}
